import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartActionButtonSx,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import SmartTablePagination, { useSmartPagination } from '../../components/SmartTablePagination';
import { formatDateTimeDisplay } from '../../utils/dateUtils';

const apiUrl = process.env.REACT_APP_API_URL;

const ClinicApprovals = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const isRtl = String(i18n.language || '').startsWith('ar');

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${apiUrl}/vetclinic-records-approval`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const nextRecords = (Array.isArray(response.data) ? response.data : [])
        .filter((record) => Number(record.approval) === 0)
        .map((record) => ({
          ...record,
          record_date: record.record_date || null,
        }))
        .sort((a, b) => new Date(b.record_date || 0) - new Date(a.record_date || 0));

      setRecords(nextRecords);
    } catch (err) {
      setError(err?.response?.data?.error || t('clinic_approvals_load_error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const filteredRecords = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return records;
    return records.filter((record) => (
      String(record.horse_name || '').toLowerCase().includes(term)
      || String(record.procedures || '').toLowerCase().includes(term)
      || String(record.record_id || '').toLowerCase().includes(term)
    ));
  }, [records, search]);
  const {
    page,
    rowsPerPage,
    paginatedItems: paginatedRecords,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSmartPagination(filteredRecords, [search]);

  const formatRecordDate = useCallback((value) => {
    return formatDateTimeDisplay(value);
  }, []);

  const proceduresFor = useCallback((value) => (
    String(value || '')
      .split('-')
      .map((procedure) => procedure.trim())
      .filter(Boolean)
  ), []);

  const openApprovalDialog = useCallback((record, approvalStatus) => {
    setPendingAction({ record, approvalStatus });
  }, []);

  const handleApproval = useCallback(async () => {
    if (!pendingAction) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      await api.put(
        `${apiUrl}/vetclinic-records/${pendingAction.record.record_id}/approval`,
        { approval: pendingAction.approvalStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecords((current) => current.filter((record) => record.record_id !== pendingAction.record.record_id));
      setMessage(t('clinic_approval_action_success'));
      setPendingAction(null);
    } catch (err) {
      setError(err?.response?.data?.error || t('clinic_approval_action_error'));
    } finally {
      setSaving(false);
    }
  }, [pendingAction, t]);

  const actionText = useMemo(() => {
    if (!pendingAction) return '';
    if (pendingAction.approvalStatus === 1) return t('approve');
    if (pendingAction.approvalStatus === 2) return t('reject');
    return t('return_for_edit');
  }, [pendingAction, t]);

  return (
    <Box
      className="smart-page-container"
      sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <SettingsPageHeader
        title={t('ClinicApprovals')}
        subtitle={t('clinic_approvals_hint')}
        count={records.length}
        countLabel={t('pending')}
        actions={(
          <Button startIcon={<RefreshIcon />} onClick={fetchRecords} disabled={loading} sx={{ color: COLORS.gold, fontWeight: 900 }}>
            {t('refresh')}
          </Button>
        )}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            label={t('search_clinic_approvals')}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: COLORS.muted, mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }} /> }}
            sx={{ maxWidth: { sm: 420 } }}
            fullWidth
          />
        </Stack>
        <TableContainer sx={smartTableContainerSx}>
          <Table size="small" sx={smartTableSx}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell align="center" sx={{ width: 72 }}>#</TableCell>
                <TableCell>{t('record_date')}</TableCell>
                <TableCell>{t('horse_name')}</TableCell>
                <TableCell>{t('procedures')}</TableCell>
                <TableCell align="center">{t('total_amount')}</TableCell>
                <TableCell align="center" sx={{ minWidth: 210 }}>{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRecords.map((record, index) => (
                <TableRow key={record.record_id} hover>
                  <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ minWidth: 170 }}><span className="date-ltr">{formatRecordDate(record.record_date)}</span></TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 900, minWidth: 180 }}>{record.horse_name || '-'}</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>
                    <Stack spacing={0.5}>
                      {proceduresFor(record.procedures).length ? proceduresFor(record.procedures).map((procedure) => (
                        <Typography key={procedure} variant="body2" sx={{ color: COLORS.text }}>
                          {procedure}
                        </Typography>
                      )) : '-'}
                    </Stack>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 900 }}>{record.total_amount ?? '-'}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t('approve')}>
                        <IconButton onClick={() => openApprovalDialog(record, 1)} sx={smartActionButtonSx(COLORS.green, COLORS.greenSoft)}>
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('reject')}>
                        <IconButton onClick={() => openApprovalDialog(record, 2)} sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)}>
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('return_for_edit')}>
                        <IconButton onClick={() => openApprovalDialog(record, 3)} sx={smartActionButtonSx(COLORS.warning, COLORS.warningSoft)}>
                          <AutorenewRoundedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('view_details')}>
                        <IconButton onClick={() => navigate(`/record-details/${record.record_id}`)} sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredRecords.length && !loading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography sx={{ color: COLORS.muted, py: 3, textAlign: 'center' }}>
                      {search ? t('no_search_results') : t('no_pending_clinic_approvals')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <SmartTablePagination
          count={filteredRecords.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog open={Boolean(pendingAction)} onClose={() => setPendingAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('confirm_clinic_approval_action')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('confirm_clinic_approval_action_message', { action: actionText })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPendingAction(null)} sx={{ color: COLORS.muted, fontWeight: 900 }}>
            {t('cancel')}
          </Button>
          <Button onClick={handleApproval} disabled={saving} variant="contained" sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>
            {actionText}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClinicApprovals;
